import React, { useEffect, useState } from "react";
import { Page } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useLocation } from "react-router-dom";
import { useForm } from "@mantine/form";
import {
  Tabs,
  TextInput,
  Textarea,
  LoadingOverlay,
  Text,
  SimpleGrid,
  Table,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCircleCheck, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import FormSection from "../../components/formSection";
import PageAndActionAccessList from "../../components/pageAndActionAccessList";
import SettingSidebarAccessList from "../../components/settingSidebarAccessList";
import FunctionList from "../../components/functionList";
import DashboardAccessEditor from "../../components/dashboardAccessEditor";
import { getAllActionKeys, ACTION_ACCESS_ACCESSIBLE } from "../../data/actionRegistry";
import { useTabActionsOptional, notifyListRefresh } from "../../context/tabContext";
import { buildTabKey, normalizePathname } from "../../utils/tabNavigation";
import {
  buildRoleEditSearch,
  buildRoleFormValuesFromData,
  resolveSettingSidebar,
} from "../../helpers/userRoleDuplicate";
import { invalidateUserRoleAccess } from "../../helpers/userRoleAccessCache";

const apiEntity = "userRole";
const ROLE_EDIT_PATH = "/operation/userRole/edit";
const DEFAULT_RETURN_PATH = "/operation/user";

const DEFAULT_ROLE_LINKS = [{ link: "MCA2", accessRight: 15 }];
const DEFAULT_SETTING_SIDEBAR = resolveSettingSidebar(DEFAULT_ROLE_LINKS);

const buildDefaultActionLinks = () =>
  getAllActionKeys().map(({ entity, action }) => ({
    entity,
    action,
    accessRight: ACTION_ACCESS_ACCESSIBLE,
  }));

const UserRoleEdit = () => {
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const tabActions = useTabActionsOptional();
  const location = useLocation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleUsers, setRoleUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [basicInfo, setBasicInfo] = useState({
    name: "",
    cname: "",
    description: "",
  });

  const query = new URLSearchParams(location.search);
  const id = query.get("id");
  const mode = query.get("mode");
  const copyFrom = query.get("copyFrom");
  const returnTo = query.get("returnTo") || DEFAULT_RETURN_PATH;
  const isAdd = mode === "add" && !id;

  const form = useForm({
    initialValues: {
      _id: null,
      name: "",
      cname: "",
      description: "",
      links: DEFAULT_SETTING_SIDEBAR.links,
      sidebarLinks: DEFAULT_SETTING_SIDEBAR.sidebarLinks,
      sidebarLinksConfigured: true,
      dashboards: [],
      actionLinks: buildDefaultActionLinks(),
      accessRight: [],
    },
  });

  const navigateToReturnPath = () => {
    const label =
      normalizePathname(returnTo) === "/operation/userRole"
        ? t("User Role")
        : t("User");
    tabNav.open({ pathname: returnTo, label });
  };

  const handleClose = () => {
    if (tabNav.enabled && tabActions) {
      const listKey = buildTabKey(normalizePathname(returnTo), "");
      const activeTabId = tabActions.getActiveTabId();
      const listTab = tabActions
        .getTabs()
        .find((tab) => tab.key === listKey && tab.id !== activeTabId);
      if (listTab) {
        tabNav.closeActive({ survivorTabId: listTab.id });
        return;
      }
    }
    navigateToReturnPath();
  };

  const loadRole = async (roleId, { duplicate = false } = {}) => {
    setLoading(true);
    try {
      const data = await api.getById({ apiEntity, id: roleId });
      if (!data) {
        throw new Error(t("Role not found"));
      }
      const values = buildRoleFormValuesFromData(data, {
        duplicate,
        defaultActionLinks: buildDefaultActionLinks(),
      });
      form.setValues(values);
      setBasicInfo({
        name: values.name,
        cname: values.cname,
        description: values.description,
      });
    } catch (error) {
      showNotification({
        title: t("Load Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadRole(id);
      return;
    }
    if (isAdd && copyFrom) {
      loadRole(copyFrom, { duplicate: true });
    }
  }, [id, isAdd, copyFrom]);

  useEffect(() => {
    if (!id || isAdd) {
      setRoleUsers([]);
      setUsersLoading(false);
      return;
    }

    let cancelled = false;
    const loadUsers = async () => {
      setUsersLoading(true);
      try {
        const result = await api.search({
          apiEntity: "user",
          searchQuery: { userRole: id },
          pageSize: 500,
          currentPage: 1,
          sort: { by: "staffId", order: "asc" },
          select: "name staffId email status",
          byAggregation: false,
        });
        if (!cancelled) setRoleUsers(result?.docs ?? []);
      } catch (error) {
        if (!cancelled) setRoleUsers([]);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [id, isAdd]);

  const handleSave = async () => {
    const { name } = basicInfo;
    if (!name?.trim()) {
      showNotification({
        title: t("Validation"),
        message: t("English name is required."),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
      return;
    }

    setSaving(true);
    try {
      const values = { ...form.values, ...basicInfo };
      delete values._id;

      let result;
      if (isAdd) {
        result = await api.add({ apiEntity, values });
      } else {
        result = await api.updateById({
          apiEntity,
          id: form.values._id,
          values,
        });
      }

      if (!result?.success) {
        throw new Error(api.getErrorMsg(result?.error));
      }

      showNotification({
        title: t("Save Success"),
        message: t("Role saved. Action and page access updates apply immediately."),
        color: "green",
        icon: <IconCircleCheck size={18} />,
      });

      invalidateUserRoleAccess();

      notifyListRefresh(returnTo);

      if (isAdd && result.data?._id) {
        tabNav.replace({
          pathname: ROLE_EDIT_PATH,
          search: buildRoleEditSearch(
            { id: result.data._id, mode: "edit" },
            returnTo
          ),
          label: `${t("User Role")}: ${basicInfo.name}`,
        });
      } else if (tabActions?.enabled && basicInfo.name?.trim()) {
        tabActions.updateTabLabelByKey(
          buildTabKey(location.pathname, location.search),
          `${t("User Role")}: ${basicInfo.name.trim()}`
        );
      }
    } catch (error) {
      showNotification({
        title: t("Save Fail"),
        message: error?.message ?? String(error),
        color: "red",
        icon: <IconExclamationMark size={18} />,
      });
    } finally {
      setSaving(false);
    }
  };

  const title = isAdd
    ? t("New User Role")
    : `${t("User Role")}: ${basicInfo.name || "..."}`;
  const usersTabLabel = usersLoading
    ? t("Users")
    : `${t("Users")} (${roleUsers.length})`;

  return (
    <Page>
      <LoadingOverlay visible={loading} />
      <PageListHeader
        title={title}
        actionButtons={[
          {
            label: t("Save Role"),
            onClick: handleSave,
            loading: saving,
          },
          {
            label: t("Close"),
            onClick: handleClose,
            variant: "default",
          },
        ]}
      />

      <Tabs defaultValue="setting" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="setting">{t("Setting")}</Tabs.Tab>
          <Tabs.Tab value="users">{usersTabLabel}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="setting" pt="md">
          <FormSection title="Basic Info">
            <SimpleGrid cols={2} breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
              <TextInput
                label={t("English Name")}
                required
                value={basicInfo.name}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setBasicInfo((prev) => ({ ...prev, name: value }));
                }}
              />
              <TextInput
                label={t("Chinese Name")}
                value={basicInfo.cname}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setBasicInfo((prev) => ({ ...prev, cname: value }));
                }}
              />
            </SimpleGrid>
            <Textarea
              label={t("Description")}
              minRows={3}
              mt="md"
              value={basicInfo.description}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setBasicInfo((prev) => ({ ...prev, description: value }));
              }}
            />
          </FormSection>

          <FormSection title="Access Control">
            <Tabs defaultValue="Pages & Actions" keepMounted={false}>
              <Tabs.List>
                <Tabs.Tab value="Pages & Actions">{t("Pages & Actions")}</Tabs.Tab>
                <Tabs.Tab value="API Control">{t("API Control")}</Tabs.Tab>
                <Tabs.Tab value="Dashboard Views">{t("Dashboard Views")}</Tabs.Tab>
                <Tabs.Tab value="Setting Page">{t("Setting Page")}</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="Pages & Actions" pt="xl">
                <PageAndActionAccessList
                  links={form.values.links}
                  actionLinks={form.values.actionLinks}
                  setFieldValue={form.setFieldValue}
                />
              </Tabs.Panel>

              <Tabs.Panel value="API Control" pt="xl">
                <Text size="sm" color="dimmed" mb="md">
                  {t("API route permissions (switches not yet wired to save).")}
                </Text>
                <FunctionList />
              </Tabs.Panel>

              <Tabs.Panel value="Dashboard Views" pt="xl">
                <DashboardAccessEditor
                  dashboards={form.values.dashboards}
                  roleName={basicInfo.name}
                  setFieldValue={form.setFieldValue}
                />
              </Tabs.Panel>

              <Tabs.Panel value="Setting Page" pt="xl">
                <SettingSidebarAccessList
                  links={form.values.links}
                  sidebarLinks={form.values.sidebarLinks}
                  setFieldValue={form.setFieldValue}
                />
              </Tabs.Panel>
            </Tabs>
          </FormSection>
        </Tabs.Panel>

        <Tabs.Panel value="users" pt="md">
          {isAdd || !id ? (
            <Text size="sm" color="dimmed">
              {t("Save this role to see the users assigned to it.")}
            </Text>
          ) : usersLoading ? (
            <Text size="sm" color="dimmed">
              {t("Loading users...")}
            </Text>
          ) : roleUsers.length === 0 ? (
            <Text size="sm" color="dimmed">
              {t("No users are assigned to this role.")}
            </Text>
          ) : (
            <Table fontSize="sm">
              <thead>
                <tr>
                  <th>{t("Staff ID")}</th>
                  <th>{t("Name")}</th>
                  <th>{t("Email")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {roleUsers.map((user) => (
                  <tr key={user._id}>
                    <td>{user.staffId}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.status}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
};

export default UserRoleEdit;
